import { siteConfig } from '../config/site'
import { getContentConfig } from './contentConfig'

/**
 * The site's contact + social details, resolved against the live `content.json`
 * contact override (PRJT-134, Slice 2). Always fully-resolved: `email`,
 * and the four social links come from `siteConfig` by default, `phone` is
 * optional (the site has no in-code phone default).
 */
export interface ResolvedContact {
  email: string
  instagram: string
  facebook: string
  tiktok: string
  youtube: string
  /** Only present when the admin has set a contact phone in content.json. */
  phone?: string
}

/** True only for a non-empty trimmed string (a real, usable override value). */
function nonEmpty(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Merge the in-code `siteConfig` contact / social with the admin-editable
 * `content.json` `contact` override. The override wins ONLY when it carries a
 * non-empty string; otherwise the hard-coded `siteConfig` value is kept.
 *
 * When no override exists - the common case, and when `content.json` is absent
 * - the returned contact is identical to today's in-code defaults, so the
 * rendered output (footer email/social, contact page) is pixel-identical.
 *
 * The first (prerender + hydration) render reads an EMPTY content store, so the
 * server and the client's first paint both use code defaults - no hydration
 * mismatch. The useBootstrap re-render swaps in the override afterwards.
 */
export function resolveContact(): ResolvedContact {
  const override = getContentConfig().contact
  return {
    email: nonEmpty(override?.email) ? override.email : siteConfig.contact.email,
    instagram: nonEmpty(override?.instagram) ? override.instagram : siteConfig.social.instagram,
    facebook: nonEmpty(override?.facebook) ? override.facebook : siteConfig.social.facebook,
    tiktok: nonEmpty(override?.tiktok) ? override.tiktok : siteConfig.social.tiktok,
    youtube: nonEmpty(override?.youtube) ? override.youtube : siteConfig.social.youtube,
    phone: nonEmpty(override?.phone) ? override.phone : undefined,
  }
}

/** The social networks the site links to, in the order the footer and contact page show them. */
export const SOCIAL_KEYS = ['instagram', 'facebook', 'tiktok', 'youtube'] as const

export type SocialKey = (typeof SOCIAL_KEYS)[number]

/**
 * The social links to show, each resolved against the content.json override
 * (BUG-387/388). One source for the footer and the contact page, so the two can
 * never disagree. A network with no URL (no override and an empty default) is
 * left out.
 */
export function resolveSocialLinks(contact: ResolvedContact = resolveContact()): { key: SocialKey; url: string }[] {
  return SOCIAL_KEYS.map((key) => ({ key, url: contact[key] })).filter((s) => nonEmpty(s.url))
}
